// Поставщики (admin/ExternalMarkets) и их аналитика (admin/ExternalMarketAnalytics).
// SteamId приходят строками (json-bigint storeAsString), суммы в рублях — копейки, в USD — десятичные.

export enum PurchaseSource {
    Marketplace = 0,
    ExternalMarket = 1,
}

export const PurchaseSourceDescription: Record<number, string> = {
    0: 'Живой продавец',
    1: 'Поставщик',
};

export enum ExternalPurchaseStatus {
    Created = 0,
    BotSelected = 1,
    Purchased = 2,
    DeliveryRequested = 3,
    TradeSent = 4,
    Received = 5,
    Failed = 6,
}

export const ExternalPurchaseStatusDescription: Record<number, string> = {
    0: 'Создан',
    1: 'Выбран бот',
    2: 'Куплено у поставщика',
    3: 'Запрошена доставка на бота',
    4: 'Трейд отправлен боту',
    5: 'Получено ботом',
    6: 'Провал',
};

export function externalPurchaseStatusColor(status: ExternalPurchaseStatus): 'primary' | 'neutral' | 'danger' | 'success' {
    switch (status) {
        case ExternalPurchaseStatus.Received:
            return 'success';
        case ExternalPurchaseStatus.Failed:
            return 'danger';
        case ExternalPurchaseStatus.Created:
            return 'neutral';
        default:
            return 'primary';
    }
}

export enum ExternalMarketErrorKind {
    Unknown = 0,
    OutOfStock = 1,
    PriceChanged = 2,
    InsufficientBalance = 3,
    TradeFailed = 4,
    Disabled = 5,
    AllAccountsBusy = 6,
    Transport = 7,
    NoBot = 8,
}

export const ExternalMarketErrorKindDescription: Record<number, string> = {
    0: 'Неизвестная ошибка',
    1: 'Нет в наличии',
    2: 'Цена вышла за допуск',
    3: 'Недостаточно средств у поставщика',
    4: 'Трейд не прошёл',
    5: 'Поставщик выключен',
    6: 'Все аккаунты заняты',
    7: 'Сеть / прокси',
    8: 'Нет подходящего бота',
};

export enum ExternalTradeState {
    Unknown = 0,
    Pending = 1,
    Sent = 2,
    Accepted = 3,
    Declined = 4,
    Canceled = 5,
    Expired = 6,
    Failed = 7,
}

export const ExternalTradeStateDescription: Record<number, string> = {
    0: 'Неизвестно',
    1: 'Создаётся',
    2: 'Отправлен',
    3: 'Принят',
    4: 'Отклонён',
    5: 'Отменён',
    6: 'Истёк',
    7: 'Ошибка',
};

/** Ручной разбор выкупа: деньги поставщику могли уйти, а предметы не прийти */
export enum ExternalPurchaseReviewState {
    None = 0,
    Open = 1,
    Resolved = 2,
}

export enum ExternalPurchaseReviewReason {
    PaidNotDelivered = 1,
    OutcomeUnknown = 2,
    AwaitingSteamOffer = 3,
    DuplicateSteamOffer = 4,
    SteamTradeStuck = 5,
}

/** Причина разбора и что с ней делать: подсказка рядом с причиной в карточке выкупа */
export const ExternalPurchaseReviewReasonDescription: Record<number, { label: string; hint: string }> = {
    1: {
        label: 'Оплачено, предметы не пришли',
        hint: 'Проверьте историю покупок и трейдов аккаунта поставщика: вернул ли он деньги (refunded) или предметы всё же пришли на бота. Закройте разбор с подходящим итогом.',
    },
    2: {
        label: 'Исход покупки неизвестен',
        hint: 'Поставщик не ответил, попытки исчерпаны. Найдите покупку в истории аккаунта поставщика: если её нет — итог «Деньги не списывались», если есть — разберите как оплаченную.',
    },
    3: {
        label: 'Ждём номер Steam-офера',
        hint: 'У бота несколько одинаковых ожидающих оферов, и он не знает, к какому заказу относится входящий Steam-офер. Сверьте номер по трейду в истории аккаунта поставщика и привяжите его до истечения офера.',
    },
    4: {
        label: 'Steam-офер привязан дважды',
        hint: 'Один Steam-офер оказался у двух оферов. Повторно он не принят. Проверьте, какому заказу он принадлежит; настоящий Steam-офер этого заказа бот привяжет сам или его можно привязать вручную.',
    },
    5: {
        label: 'Трейд Steam завис',
        hint: 'Steam-офер принят, но трейд в escrow, ошибке или откате. Проверьте трейд в Steam у бота и у поставщика.',
    },
};

export enum ExternalPurchaseReviewResolution {
    ProviderRefunded = 1,
    ItemsReceived = 2,
    LossWrittenOff = 3,
    NotPaid = 4,
    FalseAlarm = 5,
}

/** Итог разбора и когда его выбирать */
export const ExternalPurchaseReviewResolutionDescription: Record<number, { label: string; hint: string }> = {
    1: { label: 'Поставщик вернул деньги', hint: 'Покупка в истории аккаунта поставщика в статусе refunded или деньги вернулись на баланс.' },
    2: { label: 'Предметы пришли на бота', hint: 'Предметы видны в инвентаре бота или в истории его трейдов.' },
    3: { label: 'Списано в убыток', hint: 'Деньги не вернуть, предметы не получены.' },
    4: { label: 'Деньги не списывались', hint: 'В истории покупок аккаунта поставщика этой покупки нет.' },
    5: { label: 'Ложная тревога', hint: 'Предметы дошли до покупателя штатно.' },
};

export interface AdminExternalMarketInfo {
    id: number;
    gameId: number;
    isActive: boolean;
    disabledReason?: string;
    disabledBy?: string;
    disabledAt?: string;
    /** Допуск цены при выкупе, %: насколько выкуп в рублях (цена в $ × курс в момент покупки) может быть дороже себестоимости на витрине */
    maxPriceDeviationPercent: number;
    minBalanceUsd: number;
    autoDisableFailuresInRow: number;
    autoDisableUnavailableMinutes: number;
    consecutiveFailures: number;
    unavailableSince?: string;
    lastSyncAt?: string;
    lastSyncResult?: string;
    activeListings: number;
    failedPurchases24h: number;
}

export interface AdminExternalProviderInfo {
    key: string;
    name: string;
    systemUserId: string;
    /** Ник системного пользователя — продавец предметов поставщика на витрине */
    systemUserName?: string;
    partnerBotSteamIds: string[];
    connection: AdminExternalProviderConnection;
    balanceUsd?: number;
    maxAccountBalanceUsd?: number;
    markets: AdminExternalMarketInfo[];
}

export type AccountSelection = "RoundRobin" | "LeastRecentlyUsed";

/** Подключение к API поставщика. Хранится в БД, применяется без перезапуска служб. */
export interface AdminExternalProviderConnection {
    baseUrl: string;
    rateLimitPerMinute: number;
    maxConcurrentRequests: number;
    timeoutSeconds: number;
    readAccountSelection: AccountSelection;
    maxAccountSwitches: number;
    maxInFlightRetries: number;
    inFlightRetryDelayMilliseconds: number;
    maxRetryAfterWaitSeconds: number;
    retryCount: number;
    retryBaseDelayMilliseconds: number;
    maxProxyRotations: number;
    proxyRevalidateSeconds: number;
    accountsReloadSeconds: number;
    circuitBreakerFailureThreshold: number;
    circuitBreakerBaseCooldownSeconds: number;
    circuitBreakerMaxCooldownSeconds: number;
    liveFeedEnabled: boolean;
    liveFeedUrl?: string;
    liveFeedReconnectBaseDelayMilliseconds: number;
    liveFeedReconnectMaxDelaySeconds: number;
    liveFeedIdleTimeoutSeconds: number;
}

export interface UpdateExternalMarketRequest {
    /** Допуск цены при выкупе, %: насколько выкуп в рублях (цена в $ × курс в момент покупки) может быть дороже себестоимости на витрине */
    maxPriceDeviationPercent: number;
    minBalanceUsd: number;
    autoDisableFailuresInRow: number;
    autoDisableUnavailableMinutes: number;
}

export interface AdminExternalProfitRuleInfo {
    id: number;
    gameId?: number;
    minAmountExclusive: number;
    maxAmountInclusive?: number;
    fixedRate?: number;
    percentRate?: number;
    minProfit?: number;
    updated: string;
    updatedBy?: string;
}

export interface SaveExternalProfitRuleRequest {
    id?: number;
    minAmountExclusive: number;
    maxAmountInclusive?: number;
    fixedRate?: number;
    percentRate?: number;
    minProfit?: number;
}

export interface ExternalPurchasesFilter {
    gameId?: number;
    accountId?: number;
    statuses?: ExternalPurchaseStatus[];
    from?: Date;
    to?: Date;
    /** Только выкупы с открытым разбором */
    needsReview?: boolean;
    page: number;
    count: number;
}

export interface AdminExternalPurchaseTradeInfo {
    externalTradeId: string;
    steamTradeId?: string;
    state: ExternalTradeState;
}

export interface AdminExternalPurchaseInfo {
    id: number;
    orderId: number;
    gameId: number;
    accountId?: number;
    botId?: number;
    status: ExternalPurchaseStatus;
    amountUsd: number;
    rateUsdRub: number;
    costRub: number;
    plannedProfitRub: number;
    actualProfitRub?: number;
    errorKind?: ExternalMarketErrorKind;
    error?: string;
    attempts: number;
    trades: AdminExternalPurchaseTradeInfo[];
    /** Номера покупок в истории поставщика (у RustSkins — как в админке RS); появляются, пока ждём трейд */
    providerPurchaseIds: string[];
    reviewState: ExternalPurchaseReviewState;
    reviewReason?: ExternalPurchaseReviewReason;
    /** Хронология разбора: алерты, перепроверки, ручные действия — по строке на событие */
    reviewDetails?: string;
    reviewOpenedAt?: string;
    reviewResolution?: ExternalPurchaseReviewResolution;
    reviewNote?: string;
    reviewedBy?: string;
    reviewedAt?: string;
    /** Оферы поставщика на бота; приходят только в карточке заказа */
    deliveryOffers?: AdminExternalDeliveryOfferInfo[];
    created: string;
    updated: string;
}

/** Офер поставщика на бота (FromExternalMarket) */
export interface AdminExternalDeliveryOfferInfo {
    id: number;
    /** OfferStatus из models/order: Sent — ждёт Steam-офер поставщика, Accepted — принят ботом */
    status: number;
    botId?: number;
    steamOfferId?: number | string;
    sentAt?: string;
    expireAt?: string;
    /** Пометка бота: например, к каким оферам подходит неопознанный Steam-офер */
    lastError?: string;
}

/** Что сделает критическое действие. Тексты собраны сервером из текущего состояния заказа */
export interface AdminCriticalActionPreview {
    title: string;
    whatWillHappen: string;
    why: string;
    consequences: string[];
    warnings: string[];
    irreversible: boolean;
    canExecute: boolean;
    blockReason?: string;
    /** true — действие выполнено */
    executed: boolean;
}

/** Без confirm — только предпросмотр; с confirm и причиной — выполнение */
export interface AdminCriticalActionRequest {
    confirm: boolean;
    reason?: string;
}

export interface BindExternalSteamOfferRequest extends AdminCriticalActionRequest {
    offerId: number;
    /** Номер Steam-офера строкой: на сервер уходит числом без потери точности */
    steamOfferId: string;
}

export interface ResolveExternalPurchaseReviewRequest extends AdminCriticalActionRequest {
    resolution: ExternalPurchaseReviewResolution;
}

export interface AdminExternalAccountProxyInfo {
    id: number;
    address: string;
    isFailed: boolean;
    failureReason?: string;
}

/** Через какой API поставщика аккаунт выкупает. Project требует projectId. */
export type PurchaseApi = "Reseller" | "Project";

export interface AdminExternalAccountInfo {
    id: number;
    label: string;
    apiKeyMask: string;
    canRead: boolean;
    canPurchase: boolean;
    purchaseApi: PurchaseApi;
    projectId?: number;
    isActive: boolean;
    /** healthy | cooldown | disabled */
    state: string;
    cooldownUntil?: string;
    lastUsedAt?: string;
    lastError?: string;
    lastBalanceUsd?: number;
    proxy?: AdminExternalAccountProxyInfo;
}

export const ExternalAccountStateDescription: Record<string, { label: string; color: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    healthy: { label: 'В работе', color: 'success' },
    cooldown: { label: 'Остывает', color: 'warning' },
    disabled: { label: 'Выключен', color: 'danger' },
};

export interface CreateExternalAccountRequest {
    label: string;
    apiKey: string;
    canRead: boolean;
    canPurchase: boolean;
    purchaseApi: PurchaseApi;
    projectId?: number;
}

export interface UpdateExternalAccountRequest {
    label: string;
    canRead: boolean;
    canPurchase: boolean;
    purchaseApi: PurchaseApi;
    projectId?: number;
    isActive: boolean;
    /** Пусто — ключ не меняется */
    apiKey?: string;
}

export interface ExternalAccountCheckResult {
    keyValid: boolean;
    balanceUsd?: number;
    keyError?: string;
    proxyId?: number;
    proxyOk?: boolean;
}

export interface ExternalProxiesSummary {
    total: number;
    failed: number;
    free: number;
    activeAccounts: number;
}

export interface AdminExternalAuditInfo {
    id: number;
    gameId?: number;
    action: string;
    changedBy: string;
    oldValue?: string;
    newValue?: string;
    created: string;
}

// ---------- Аналитика ----------

export enum StatsGranularity {
    Hour = 0,
    Day = 1,
    Month = 2,
    Year = 3,
    All = 4,
}

export const StatsGranularityDescription: Record<number, string> = {
    0: 'Час',
    1: 'День',
    2: 'Месяц',
    3: 'Год',
    4: 'Весь период',
};

/** Доли — проценты 0..100, суммы — копейки */
export interface ExtShareRow {
    gameId?: number;
    gameName: string;
    totalCount: number;
    totalSum: number;
    externalCount: number;
    externalSum: number;
    shareByCount: number;
    shareBySum: number;
    costRub: number;
    plannedProfitRub: number;
    actualProfitRub: number;
    profitDeviationRub: number;
}

export interface ExtProviderRow extends ExtShareRow {
    providerKey: string;
    providerName: string;
    shareOfExternal: number;
    avgProfitPercent: number;
    failedCount: number;
    failedRate: number;
}

export interface ExtTimeseriesPoint {
    bucketUtc: string;
    totalCount: number;
    totalSum: number;
    externalCount: number;
    externalSum: number;
    share: number;
}

export interface ExtFailureRow {
    errorKind: ExternalMarketErrorKind;
    count: number;
    lostSum: number;
}

export interface ExtTopItem {
    gameId: number;
    skinName: string;
    count: number;
    sum: number;
    actualProfitRub: number;
}

export interface ExtAnalyticsParams {
    from: Date;
    to: Date;
    gameId?: number;
    providerKey?: string;
    granularity?: StatsGranularity;
    take?: number;
}
